import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { stats } from '../mock';

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 lg:py-32 bg-gradient-to-br from-gray-900 via-purple-900/20 to-black overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* Content */}
          <motion.div variants={itemVariants} className="space-y-8">
            <div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-6 uppercase">
                About MasterPlan
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-purple-500 mb-8" />
            </div>

            <div className="space-y-6 text-gray-300 text-lg leading-relaxed" style={{ maxHeight: '250px', overflowY: 'scroll' }}>
              <p className="text-xl font-light">
                What do you get when a bunch of die-hard music lovers and youth ministers who love Jesus, the Church, and youth cross paths and come together?
              </p>
              <p> Ta-da! Thus goes the story of{' '}<span className="text-green-400 font-semibold">MasterPlan.</span> <span className="text-purple-400 font-semibold"></span></p>
              <div >
                <p>
                  <span className="text-green-400 font-semibold">MasterPlan.</span>
                  <span className="text-purple-400 font-semibold"></span> From its humble beginnings at a small but vibrant Jesus Youth Gathering in Dubai, MasterPlan has grown into a household name for many music-loving Catholics worldwide. Their songs, such as “Tungu-Nungu” and “Christyle,” are poignant reminders that Catholics know a thing or two about dance and mosh pits.
                </p>
                <p>
                  Heavily influenced by RexBand and other similar vibrant musical acts, a typical MasterPlan concert checks all the boxes to keep you on your feet. Their singers engage with the audience with joy and ease, leaving smiles that last long after the concert is over. Being youth and teen ministers themselves, MasterPlan is built on a simple principle: Mission before Music—an unwavering belief that the joy they have found must be proclaimed far and wide to every soul, with music serving as just a tool.
                </p>
                <p>MasterPlan has had its fair share of blessings. Their commitment to the great commission has taken them far and wide—performing at youth festivals and as main acts at World Youth Days in Rio de Janeiro, Kraków, Panama, and Lisbon, as well as at the CCR Jubilee Main stages at Rome. They have supported conferences and retreats across Middle East, Europe, the Indian subcontinent and Australia and have served in missions to Africa and beyond. With four albums, ten singles, and over a million streams across video and music platforms, MasterPlan truly understands the power of music in proclaiming the Gospel.</p>
                <p>
                  Programs featuring MasterPlan are a double whammy. They not only perform but also serve as resources, delivering quality sessions on Music Ministry, Praise and Worship, and other youth, teens and family - relevant topics. And yet, after gruelling stage shows and ministry, MasterPlan’s way of unwind is simply hanging out with people—seamlessly blending fun and wit with honest and powerful personal testimonies.
                </p>
                <p>
                  Immensely talented yet constantly grounded, MasterPlan belts out their songs fully live, and yet offstage, they come across like long-lost friends. Some say it’s because of the deep chemistry between them that’s aged like fine wine.
                </p>
                <p>
                  Those who know the band closer know that it’s simply the joy of knowing Jesus.
                </p>
              </div>
            </div>

            <motion.div
              variants={itemVariants}
              className="pt-6"
            >
              {/* <a
                href="#band"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white font-bold py-4 px-8 rounded-full text-base uppercase tracking-wider shadow-xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105"
              >
                Meet The Band
              </a> */}
            </motion.div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative bg-gradient-to-br from-purple-900/40 to-gray-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8 hover:border-green-400/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-purple-500/0 group-hover:from-green-500/10 group-hover:to-purple-500/10 rounded-2xl transition-all duration-300" />
                <div className="relative z-10">
                  <div className="text-1xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-purple-400 mb-3">
                    {stat.label}
                  </div>
                  <div className="text-gray-300 font-medium text-sm uppercase tracking-wide">
                    {stat.description}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <template>
        <div className="container mx-auto px-6 lg:px-16 relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            {/* Content */}
            <motion.div variants={itemVariants} className="space-y-8">
              <div>
                <h2 className="text-5xl md:text-6xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-6 uppercase">
                  About MasterPlan
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-purple-500 mb-8" />
              </div>

              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p className="text-xl font-light">
                  MasterPlan is a <span className="text-green-400 font-semibold">Dubai-based Christian worship band</span> with over{' '}
                  <span className="text-purple-400 font-semibold">15 years of experience</span> performing at major youth events across the Middle East.
                </p>
                <p>
                  Since 2009, we have been privileged to perform on over{' '}
                  <span className="text-green-400 font-semibold">200+ main stages</span>, inspiring{' '}
                  <span className="text-purple-400 font-semibold">thousands of young people</span> through powerful worship and heartfelt music.
                </p>
                <p>
                  From the Youth Arise International Gathering in Brazil to the Catholic Youth Conference of Arabia,
                  our mission has always been to connect hearts and souls through the universal language of music.
                </p>
              </div>

              <motion.div
                variants={itemVariants}
                className="pt-6"
              >
                {/* <a
                href="#band"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white font-bold py-4 px-8 rounded-full text-base uppercase tracking-wider shadow-xl hover:shadow-purple-500/50 transition-all duration-300 hover:scale-105"
              >
                Meet The Band
              </a> */}
              </motion.div>
            </motion.div>

            {/* Stats Cards */}
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group relative bg-gradient-to-br from-purple-900/40 to-gray-900/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8 hover:border-green-400/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-purple-500/0 group-hover:from-green-500/10 group-hover:to-purple-500/10 rounded-2xl transition-all duration-300" />
                  <div className="relative z-10">
                    <div className="text-4xl md:text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-purple-400 mb-3">
                      {stat.label}
                    </div>
                    <div className="text-gray-300 font-medium text-sm uppercase tracking-wide">
                      {stat.description}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </template>
    </section>
  );
};
